import React, {
  useEffect,
  useState,
  Fragment,
  useRef,
  SyntheticEvent,
} from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';

import { Theme } from '@app/theme';
import { Logger } from 'lib/logger';
import { MenuWindow, MenuItem, Button } from '@app/core';
import { isAdminSelector } from 'store';

const useStyles = createUseStyles((theme: Theme) => ({
  select: {
    position: 'relative',
  },
  caret: {
    fontSize: theme.iconSize.regular,
    marginLeft: theme.marginBase / 2,
  },
  icon: {
    fontSize: theme.iconSize.regular,
    marginRight: theme.marginBase,
  },
  dropdownItem: {
    display: 'flex',
    alignItems: 'center',
  },
  itemsBox: {
    marginTop: theme.marginBase / 2,
    boxShadow: theme.boxShadow.base,
  },
}));

interface Option {
  value: string;
  icon?: string;
  label?: string;
  adminOnly?: boolean;
}

interface Props {
  onChange: (value: string, option: Option) => any;
  value: string;
  options: Option[];
  className?: string;
}

export const FieldTypeDropdown = ({
  value,
  options,
  onChange,
  className,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  const [option, setOption] = useState<Option | null>(null);
  const [anchorElmnt, setAnchorElmnt] = useState<null | Element>(null);
  const idRef = useRef(`dots_menu_${Math.random()}`);
  const isAdmin = useSelector(isAdminSelector);

  const onClick = (event: SyntheticEvent) => {
    event.stopPropagation();
    setAnchorElmnt(event.currentTarget);
  };
  const onClose = (event: SyntheticEvent) => {
    event.stopPropagation();
    setAnchorElmnt(null);
  };

  useEffect(() => {
    const foundOption = options.find((o) => o.value === value);
    if (!foundOption || foundOption === option) {
      return;
    }
    setOption(foundOption);
  }, [options, value, option]);

  const onSelect = (val: string) => {
    const newOption = options.find((o) => o.value === val);
    if (!newOption) {
      Logger.assertionFailed('Option not found', { newOption, val });
      return;
    }
    return onChange(val, newOption);
  };

  return (
    <Fragment>
      <Button
        variant="muted"
        color="blue"
        small
        className={className}
        onClick={onClick}
        aria-controls={idRef.current}
        aria-haspopup="true"
      >
        <i className={classnames(option && option.icon, classes.icon)} />
        {option && option.label}
        <i className={classnames('ri-arrow-down-s-line', classes.caret)} />
      </Button>
      <MenuWindow
        open={!!anchorElmnt}
        onClose={onClose}
        onClick={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        className={classes.itemsBox}
        anchorEl={anchorElmnt}
        id={idRef.current}
      >
        {options.map(
          (sOption) =>
            (!sOption.adminOnly || isAdmin) && (
              <MenuItem
                value={sOption.value}
                key={sOption.value}
                selected={sOption.value === value}
                onSelect={onSelect}
              >
                <div className={classes.dropdownItem}>
                  <i
                    className={classnames(
                      sOption && sOption.icon,
                      classes.icon,
                    )}
                  />
                  {sOption.label}
                </div>
              </MenuItem>
            ),
        )}
      </MenuWindow>
    </Fragment>
  );
};
